/**
 * ChatStore - LocalStorage based state management for Chat
 * 
 * Manages:
 * - Messages per chat
 * - Unseen counts
 * - Active chat state
 * - Persistence
 */

const STORAGE_KEY = 'chatState';

class ChatStore {
    constructor() {
        this.subscribers = [];
        this.state = this.loadState();
    }

    // --- State Management ---

    loadState() {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (e) {
            console.error("Failed to load chat state", e);
        }
        return this.getInitialState();
    }

    getInitialState() {
        return {
            chats: {}, // Map<chatId, { messages: [], unseenCount: 0, lastMessage: {} }>
            activeChatId: null
        };
    }

    saveState() {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
            this.notifySubscribers();
        } catch (e) {
            console.error("Failed to save chat state", e);
        }
    }

    // --- Actions ---

    /**
     * Send a message
     * @param {string} chatId 
     * @param {string} content 
     * @param {object} senderInfo { id, type, name }
     */
    sendMessage(chatId, content, senderInfo) {
        const message = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            chatId,
            sender: { ...senderInfo },
            content,
            timestamp: new Date().toISOString(),
            status: 'sent',
            isUnseen: false, // Sent messages are seen by definition
            isMe: true, // Flag for UI helper
        };

        // Auto-fix for sender identity if user info is passed
        // In a real app we'd validate against a session token


        this.addMessageToState(chatId, message);

        // Simulate delivery/seen for effect (in a real app this comes from socket)
        setTimeout(() => this.updateMessageStatus(chatId, message.id, 'delivered'), 1000);
        setTimeout(() => this.updateMessageStatus(chatId, message.id, 'seen'), 2500);

        return message;
    }

    /**
     * Receive a message (Simulation/Socket handler)
     * @param {string} chatId 
     * @param {string} content 
     * @param {object} senderInfo 
     */
    receiveMessage(chatId, content, senderInfo) {
        const isChatActive = this.state.activeChatId === chatId;

        const message = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            chatId,
            sender: { ...senderInfo },
            content,
            timestamp: new Date().toISOString(),
            status: 'delivered',
            isUnseen: !isChatActive, // If chat is not open, it's unseen
            isMe: false,
            isRead: false
        };

        this.addMessageToState(chatId, message);

        if (isChatActive) {
            // If active, mark as seen immediately (or slightly delayed)
            setTimeout(() => this.updateMessageStatus(chatId, message.id, 'seen'), 500);
        }
    }

    addMessageToState(chatId, message) {
        const chat = this.state.chats[chatId] || { messages: [], unseenCount: 0, lastMessage: null };

        chat.messages.push(message);
        chat.lastMessage = message;

        if (message.isUnseen) {
            chat.unseenCount = (chat.unseenCount || 0) + 1;
        }

        this.state.chats[chatId] = chat;
        this.saveState();
    }

    updateMessageStatus(chatId, messageId, status) {
        const chat = this.state.chats[chatId];
        if (!chat) return;

        const msgIndex = chat.messages.findIndex(m => m.id === messageId);
        if (msgIndex !== -1) {
            chat.messages[msgIndex].status = status;
            // Also update lastMessage if it matches
            if (chat.lastMessage && chat.lastMessage.id === messageId) {
                chat.lastMessage.status = status;
            }
            this.saveState();
        }
    }

    openChat(chatId) {
        this.state.activeChatId = chatId;

        // Reset unseen count and mark messages as seen
        const chat = this.state.chats[chatId];
        if (chat) {
            chat.unseenCount = 0;
            chat.messages.forEach(m => {
                if (!m.isMe) m.isUnseen = false;
                if (!m.isMe && m.status !== 'seen') m.status = 'seen';
            });
        }

        this.saveState();
    }

    closeChat() {
        this.state.activeChatId = null;
        this.saveState();
    }

    markAsSeen(chatId) {
        // Logic duplicates openChat mostly, but can be explicit
        this.openChat(chatId);
    }

    /**
     * Syncs messages from API/External source
     * @param {string} chatId 
     * @param {Array} externalMessages 
     */
    syncMessages(chatId, externalMessages) {
        const chat = this.state.chats[chatId] || { messages: [], unseenCount: 0, lastMessage: null };
        let updates = 0;
        const existingIds = new Set(chat.messages.map(m => String(m.id)));

        externalMessages.forEach(extMsg => {
            // Normalize ID
            const msgId = String(extMsg.id);
            if (!existingIds.has(msgId)) {
                // New message found
                // Map API format to Store format
                const mappedMsg = {
                    id: msgId,
                    chatId: chatId,
                    sender: {
                        id: extMsg.sender_id || extMsg.senderId,
                        type: extMsg.sender_type || extMsg.senderType,
                        name: extMsg.sender_name || extMsg.senderName
                    },
                    content: extMsg.message || extMsg.content,
                    timestamp: extMsg.created_at || extMsg.timestamp,
                    status: extMsg.is_read ? 'seen' : 'delivered',
                    isUnseen: !extMsg.is_read, // Start with API status, but logic below overrides if needed
                    isMe: false, // defaults
                };

                // Refine isMe
                // We need to know who "Me" is.
                // If the message sender_type matches "admin" (assuming we are admin here), it's me.
                // But better: use the 'isFromCurrentUser' from formatter if available, or deduce.
                if (extMsg.sender_type === 'admin') mappedMsg.isMe = true;
                if (extMsg.isFromCurrentUser) mappedMsg.isMe = true; // if pre-formatted

                if (mappedMsg.isMe) {
                    mappedMsg.status = 'sent'; // or delivered/seen depending on api
                    mappedMsg.isUnseen = false;
                } else {
                    // It's from them.
                    // If chat is active, it's seen.
                    if (this.state.activeChatId === chatId) {
                        mappedMsg.isUnseen = false;
                        mappedMsg.status = 'seen';
                    }
                }

                chat.messages.push(mappedMsg);
                updates++;

                // Increment unseen only if it's NOT me and it IS unseen
                // AND we definitely didn't have it before.
                if (!mappedMsg.isMe && mappedMsg.isUnseen) {
                    chat.unseenCount = (chat.unseenCount || 0) + 1;
                }
                // Logic Check: if we sync 50 messages, we don't want unseenCount += 50 if they are old.
                // Re-evaluating Unseen: 
                // The API 'is_read' is usually reliable for history.
                // So relying on !extMsg.is_read is reasonably safe for history.
            }
            else {
                // Message already exists in store. If history shows it as read, update existing message status.
                try {
                    const existingIndex = chat.messages.findIndex(m => String(m.id) === msgId);
                    if (existingIndex !== -1) {
                        const existingMsg = chat.messages[existingIndex];
                        const historyIsRead = !!extMsg.is_read;
                        if (historyIsRead && existingMsg.status !== 'seen') {
                            // Update status and unseen flag
                            existingMsg.status = 'seen';
                            if (!existingMsg.isMe) existingMsg.isUnseen = false;
                            // Decrement unseenCount if it was previously counted as unseen
                            if (chat.unseenCount && chat.unseenCount > 0 && !existingMsg.isMe) {
                                chat.unseenCount = Math.max(0, chat.unseenCount - 1);
                            }
                            updates++;
                        }
                    }
                } catch (e) {
                    // ignore per-message update errors
                    console.warn('ChatStore.syncMessages: failed to update existing message read status', e);
                }
            }
        });

        if (updates > 0) {
            // Sort by timestamp
            chat.messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            chat.lastMessage = chat.messages[chat.messages.length - 1];
            this.state.chats[chatId] = chat;
            this.saveState();
        }
    }

    // --- Getters ---

    getMessages(chatId) {
        return this.state.chats[chatId]?.messages || [];
    }

    getUnseenCount(chatId) {
        return this.state.chats[chatId]?.unseenCount || 0;
    }

    getTotalUnseenCount() {
        return Object.values(this.state.chats).reduce((acc, chat) => acc + (chat.unseenCount || 0), 0);
    }

    getChat(chatId) {
        return this.state.chats[chatId];
    }

    // --- Subscription ---

    subscribe(callback) {
        this.subscribers.push(callback);
        return () => {
            this.subscribers = this.subscribers.filter(cb => cb !== callback);
        };
    }

    notifySubscribers() {
        this.subscribers.forEach(cb => cb(this.state));
    }
}

export const chatStore = new ChatStore();
